import {
	ISegmentList,
	useSegmentFilterCascaded,
	ICompanySegmentList,
	useCompanySegmentFilterCascaded,
} from "@repo/baseCloud";
import {
	updatePropertyFn,
	updateValuesWrapper,
	useMultiplePropUpdates,
} from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { clone, hasValue, log } from "@repo/helpers";
import { PushProcessingNotification } from "@repo/helpers/src/notificationActions";
import * as validate from "@repo/helpers/src/validators";
import { FilterItem, KeyedArray, keyPair } from "@repo/models";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { RetailPartner } from "../../../api/locationApi";
import { useLocationApi } from "../../../api/locationApi";
import { useQueryWrapper } from "../../../api/locationPortalApi";
import { getItems, useFilterItemQuery } from "../../../api/queries";
import * as Queries from "../../../api/queries";
import { useSettingsApi } from "../../../api/settingsApi";
import { useStatesApi } from "../../../api/statesApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useLocationContext } from "../../../contexts/editableItemContext";
import { useUserContext } from "../../../contexts/userContext";
import { useCompanySegmentCascade, useSegmentCascade } from "../../../helpers/queryHooks";
import { tabs } from "../../../string";

export const LocationEditor: FC = () => {
	const history = useHistory();
	const locationContext = useLocationContext();
	const { user } = useUserContext();
	const locationApi = useLocationApi();
	const statesApi = useStatesApi();
	const { useSettingsQuery } = useSettingsApi();
	const { location, locationId, locationLoaded, overwriteItem } = locationContext;
	const [activeSegmentLists, activeSegmentListsLoaded] = useLocationSegmentFilter();
	const [activeCompanySegmentLists, activeCompanySegmentListsLoaded] = useCompanySegmentFilter();
	const [activeInfoLists, activeLocationInfoListsLoaded] = useLocationInfoLists();
	const { data: settings, loaded: settingsLoaded } = useSettingsQuery(user?.userId, locationId);

	useEffect(() => {
		if (!locationLoaded || locationId) return;
		const newLocation = clone(location);
		newLocation.details = { mobileApproved: true };
		overwriteItem(newLocation);
	}, [locationLoaded, locationId]);

	const templateData = useMemo(() => {
		return {
			activeSegmentLists,
			activeCompanySegmentLists,
			activeInfoLists,
		};
	}, [activeSegmentLists, activeCompanySegmentLists, activeInfoLists]);

	const loaded =
		locationLoaded &&
		settingsLoaded &&
		activeSegmentListsLoaded &&
		activeCompanySegmentListsLoaded &&
		activeLocationInfoListsLoaded;

	const [originalStatus] = useState(location.statusId);

	async function updateValueInternal(
		id: string,
		value: any,
		currentState: ILocationInfoData,
		parentComponent?: string,
	) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return (newState ?? currentState) as ILocationInfoData;
	}
	const cascadeSegment = useSegmentCascade(updateValueInternal, "location");
	const cascadeCompanySegment = useCompanySegmentCascade(updateValueInternal, "location");
	const customUpdates = useMultiplePropUpdates(
		cascadeSegment,
		cascadeCompanySegment,
		applyBusinessLogic,
	) as updatePropertyFn<ILocationInfoData>;

	const updateValues = useMemo(() => updateValuesWrapper<ILocationInfoData>(customUpdates), [customUpdates]);

	async function applyBusinessLogic(id: string, value: any, newState: ILocationInfoData) {
		const update = async (name: string, value: any) =>
			(newState = await updateValueInternal(name, value, newState));
		const clear = async (name: string) => await update(name, null);

		if (id.includes("zip")) {
			const updatedState = await getCityFromZip(value, id, newState);
			newState = updatedState || newState;
		}

		const { location } = newState;
		const { mobileApprovedDefaultFalse } = settings;
		switch (id) {
			case "location.partnerTypeId":
				await clear("location.partnerId");
				await clear("location.partnerName");
				break;
			case "location.statusId":
				await update("location.statusChanged", hasValue(value) && originalStatus !== value);
				break;
			case "location.storeNumber":
				//Autogenerate location code based on partner code and store number
				const partnerCode = activeInfoLists.retailPartnerCodes.find((p) => p.id === location.partnerId)?.value;
				if (partnerCode) await update("location.locationCode", `${partnerCode}${value}`);
				break;
			case "location.partnerName":
				if (!location.locationId && mobileApprovedDefaultFalse.includes(value)) {
					await update("location.details.mobileApproved", false);
				} else if (!location.locationId && !mobileApprovedDefaultFalse.includes(value)) {
					await update("location.details.mobileApproved", true);
				}
				break;
		}
		return newState as ILocationInfoData;

		async function getCityFromZip(zipCode: string, property: string, newState: ITemplateData) {
			const propertyParts = property.split(".");
			const propertyPrefix = propertyParts.slice(0, propertyParts.length - 1).join(".");
			try {
				if (!zipCode || zipCode.trim() === "" || validate.zipCode(zipCode) !== null) {
					await update(propertyPrefix + ".stateId", undefined);
					await update(propertyPrefix + ".stateName", undefined);
					await update(propertyPrefix + ".city", undefined);
				} else {
					const stateInformation = await statesApi.getCityByZip(zipCode);
					await update(propertyPrefix + ".stateId", stateInformation.stateId);
					await update(propertyPrefix + ".stateName", stateInformation.stateName);
					await update(propertyPrefix + ".city", stateInformation.city);
				}
			} catch (e) {
				log(e, "error");
			}
			return newState;
		}
	}

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Location...");
		try {
			const newId = await locationApi.saveLocation(location);

			const newLocation = await locationApi.loadLocation(newId);

			notify.success("Location saved");

			if (newId !== location.locationId) {
				history.push(`/${newId}/edit`);
			}

			return newLocation;
		} catch (ex) {
			notify.error("Failed to save location");
			throw ex;
		}
	}, [location, history, locationApi]);

	return (
		<EditableItemDynamicTab
			itemContext={locationContext}
			pageName={tabs.locationInfo}
			otherTemplateData={templateData}
			itemProp="location"
			updateValues={updateValues}
			save={save}
			loaded={loaded}
		/>
	);
};

interface ILocationInfoData extends ITemplateData {
	activeSegmentLists?: ILocationSegmentList;
	activeLocationInfoLists?: ILocationInfoLists;
}

interface ILocationInfoLists {
	countries: keyPair[];
	states: keyPair[];
	retailPartnerTypes: keyPair[];
	retailPartners: keyPair[];
	retailPartnerCodes: keyPair[];
}

interface ILocationSegmentList extends ISegmentList<keyPair[]> {}
interface ILocationCompanySegmentList extends ICompanySegmentList<keyPair[]> {}
/**
 * Checks selected items, and filters other dropdown options based on selection (i.e. filters marketers based on channel)
 * Does this by checking which branches are associated with selected items
 */
function useLocationSegmentFilter() {
	const { location } = useLocationContext();

	const activeSegmentLists = useSegmentFilterCascaded<keyPair[]>(location, (i: KeyedArray<FilterItem>) =>
		i?.map((c) => {
			return { id: c.id, value: c.name ?? c.value };
		}),
	) as ILocationSegmentList;

	const loaded =
		activeSegmentLists.brandsLoaded &&
		activeSegmentLists.channelsLoaded &&
		activeSegmentLists.marketersLoaded &&
		activeSegmentLists.statesLoaded &&
		activeSegmentLists.branchesLoaded;

	return [activeSegmentLists, loaded] as const;
}

function useCompanySegmentFilter() {
	const { location } = useLocationContext();

	const activeSegmentLists = useCompanySegmentFilterCascaded<keyPair[]>(location, (i: KeyedArray<FilterItem>) =>
		i?.map((c) => {
			return { id: c.id, value: c.name ?? c.value };
		}),
	) as ILocationCompanySegmentList;

	const loaded =
		activeSegmentLists.companiesLoaded &&
		activeSegmentLists.divisionsLoaded &&
		activeSegmentLists.regionsLoaded &&
		activeSegmentLists.marketsLoaded;

	return [activeSegmentLists, loaded] as const;
}

function useLocationInfoLists() {
	const { location, locationLoaded } = useLocationContext();
	const {
		getBrands,
		getChannels,
		getCompanyDivisions,
		getStatesByCountry,
		getCountries,
		getRetailPartnerTypes,
		getRetailPartners,
	} = useLocationApi();
	const [brands, brandsLoaded] = useFilterItemQuery(Queries.PORTAL.BRANDS, getBrands);
	const [channels, channelsLoaded] = useFilterItemQuery(Queries.PORTAL.CHANNELS, getChannels);
	const [companyDivisions, companyDivisionsLoaded] = useFilterItemQuery(
		Queries.PORTAL.COMPANY_DIVISIONS,
		getCompanyDivisions,
	);
	const [countries, countriesLoaded] = useFilterItemQuery(Queries.PORTAL.COUNTRIES, getCountries);
	const { data: allStates, loaded: statesLoaded } = useQueryWrapper(
		[Queries.STATES_BY_COUNTRY],
		[],
		getStatesByCountry,
	);
	const [retailPartnerTypes, retailPartnerTypesLoaded] = useFilterItemQuery(
		[Queries.RETAIL_PARTNER_TYPES],
		getRetailPartnerTypes,
	);
	const [allRetailPartners, retailPartnersLoaded] = useFilterItemQuery<RetailPartner>(
		[Queries.RETAIL_PARTNERS],
		getRetailPartners,
	);
	const retailPartners = useMemo(
		() =>
			!allRetailPartners
				? null
				: allRetailPartners.filter((c) => !location.partnerTypeId || location.partnerTypeId === c.typeId),
		[allRetailPartners, location.partnerTypeId],
	);
	const states = useMemo(
		() =>
			new KeyedArray(
				!allStates
					? null
					: allStates
							.map((s) => ({ id: s.usStateId, name: s.name, countryId: s.countryId }))
							.filter((c) => !location.countryId || c.countryId === location.countryId),
				(i) => i.id,
			),
		[allStates, location.countryId],
	);

	const loaded =
		brandsLoaded &&
		channelsLoaded &&
		companyDivisionsLoaded &&
		countriesLoaded &&
		statesLoaded &&
		retailPartnerTypesLoaded &&
		retailPartnersLoaded &&
		locationLoaded;

	const lists = useMemo(() => {
		return {
			brands: getItems(brands, brandsLoaded),
			channels: getItems(channels, channelsLoaded),
			companyDivisions: getItems(companyDivisions, companyDivisionsLoaded),
			countries: getItems(countries, countriesLoaded),
			states: getItems(states, statesLoaded),
			retailPartnerTypes: getItems(retailPartnerTypes, retailPartnerTypesLoaded),
			retailPartners: getItems(retailPartners, retailPartnersLoaded),
			retailPartnerCodes: retailPartners?.map((p) => ({ id: p.id, value: p.value }) as keyPair),
		} as ILocationInfoLists;
	}, [
		brands,
		brandsLoaded,
		channels,
		channelsLoaded,
		companyDivisions,
		companyDivisionsLoaded,
		countries,
		countriesLoaded,
		states,
		statesLoaded,
		retailPartnerTypes,
		retailPartnerTypesLoaded,
		retailPartners,
		retailPartnersLoaded,
	]);

	return [lists, loaded] as const;
}
